import React from 'react';
import { useSelector } from 'react-redux';
import { Box, Container, Typography, Link, IconButton } from '@mui/material';
import {
  Facebook as FacebookIcon,
  Instagram as InstagramIcon,
  MusicNote as TikTokIcon,
  Twitter as XIcon,
  LinkedIn as LinkedInIcon,
} from '@mui/icons-material';
import TextIcon from '../../components/common/TextIcon';
import { getTemplateStyle } from '../theme/templateStyles';

const Footer = () => {
  const { businessName, businessPhone, businessEmail, businessAddress } = useSelector((state) => state.configs);
  const { socialMedia, workingHours, logo, template } = useSelector((state) => state.template);
  const minimalFooter = getTemplateStyle(template).footer === 'minimal';

  const contactInfo = [
    ...(businessAddress ? [
      { label: 'Location:', href: '#', isText: true, isTitle: true },
      { label: businessAddress, href: '#', isText: true },
    ] : []),
    ...(businessPhone ? [
      { label: 'Phone:', href: '#', isText: true, isTitle: true },
      { label: businessPhone, href: `tel:${businessPhone}` },
    ] : []),
    ...(businessEmail ? [
      { label: 'Email:', href: '#', isText: true, isTitle: true },
      { label: businessEmail, href: `mailto:${businessEmail}` },
    ] : []),
  ];

  const workingHoursLines = (workingHours?.enabled && workingHours?.lines?.length > 0)
    ? workingHours.lines.map((line) => ({ label: line.label ? `${line.label}: ${line.value}` : line.value, href: '#', isText: true }))
    : [];

  const footerLinks = {
    'Useful Links': [
      { label: 'About Us', href: '/about' },
      { label: 'Contact', href: '/contact' },
      { label: 'FAQ', href: '/faqs' },
      { label: 'Delivery Info', href: '/shipping' },
      { label: 'Returns Policy', href: '/returns' },
    ],
    ...(contactInfo.length > 0 ? { 'Contact Info': contactInfo } : {}),
    ...(workingHoursLines.length > 0 ? { 'Working Hours': workingHoursLines } : {}),
  };

  // Minimal footer variant: single centered band instead of link columns
  if (minimalFooter) {
    const minimalLinks = [
      { label: 'Products', href: '/products' },
      { label: 'Contact', href: '/contact' },
      { label: 'FAQ', href: '/faqs' },
      { label: 'Delivery', href: '/shipping' },
      { label: 'Returns', href: '/returns' },
      { label: 'Terms', href: '/terms' },
    ];
    return (
      <Box sx={{ backgroundColor: '#000000', color: '#ffffff', mt: 'auto' }}>
        <Container maxWidth="lg" sx={{ py: { xs: 3, md: 4 }, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2, textAlign: 'center' }}>
          {logo?.logoType === 'logo' && logo?.logoPath ? (
            <Box
              component="img"
              src={logo.logoPath}
              alt={`${logo?.logoText || businessName || 'Store'} logo`}
              sx={{ height: '40px', width: 'auto', objectFit: 'contain' }}
            />
          ) : (
            <Typography sx={{ fontSize: '1.1rem', fontWeight: 300, letterSpacing: '0.25em', textTransform: 'uppercase' }}>
              {logo?.logoText || businessName || 'Store'}
            </Typography>
          )}
          <Box sx={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'center', gap: { xs: 1.5, md: 3 } }}>
            {minimalLinks.map((link) => (
              <Link key={link.href} href={link.href} sx={{ fontSize: '0.75rem', color: 'rgba(255,255,255,0.75)', textDecoration: 'none', letterSpacing: '0.08em', textTransform: 'uppercase', '&:hover': { color: '#fff' } }}>
                {link.label}
              </Link>
            ))}
          </Box>
          <Typography sx={{ fontSize: '0.65rem', color: 'rgba(255,255,255,0.5)' }}>
            © {new Date().getFullYear()} {businessName || 'Store'}. All Rights Reserved.
          </Typography>
        </Container>
      </Box>
    );
  }

  return (
    <Box sx={{ backgroundColor: '#000000', color: '#ffffff', mt: 'auto' }}>
      <Container maxWidth={false} sx={{ px: { xs: 2, md: 4 }, py: { xs: 3, md: 4.5 } }}>
        {/* Logo Row - Compact */}
        <Box sx={{
          pb: 1.5,
          mb: 2,
          borderBottom: '1px solid rgba(255,255,255,0.1)'
        }}>
          {logo?.logoType === 'logo' && logo?.logoPath ? (
            <Box
              component="img"
              src={logo.logoPath}
              alt={`${logo?.logoText || businessName || 'Store'} logo`}
              sx={{ height: { xs: '40px', md: '50px' }, width: 'auto', objectFit: 'contain' }}
            />
          ) : (
            <TextIcon text={logo?.logoText || businessName?.charAt(0)?.toUpperCase() || 'A'} width={50} height={50} />
          )}
        </Box>

        {/* Links Section - Equal Width Layout */}
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: 'repeat(2, 1fr)', sm: 'repeat(2, 1fr)', md: 'repeat(4, 1fr)' }, gap: { xs: 1.5, md: 2 }, mb: 2 }}>
          {/* Useful Links */}
          <Box>
            <Typography
              sx={{
                fontWeight: 600,
                fontSize: '0.7rem',
                mb: 0.75,
                textTransform: 'uppercase',
                letterSpacing: '0.05em',
              }}
            >
              Useful Links
            </Typography>
            <Box sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)' },
              gap: { xs: 0.25, sm: 0.75 },
              alignItems: 'start',
              maxWidth: '280px'
            }}>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3.5 }}>
                {footerLinks['Useful Links'].slice(0, 4).map((link) => (
                  <Link
                    key={link.label}
                    href={link.href}
                    sx={{
                      color: '#ffffff',
                      textDecoration: 'none',
                      fontSize: '0.65rem',
                      lineHeight: 1.8,
                      whiteSpace: 'nowrap',
                      '&:hover': {
                        color: '#ffffff',
                        textDecoration: 'underline',
                      },
                    }}
                  >
                    {link.label}
                  </Link>
                ))}
              </Box>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3.5 }}>
                {footerLinks['Useful Links'].slice(3, 7).map((link) => (
                  <Link
                    key={link.label}
                    href={link.href}
                    sx={{
                      color: '#ffffff',
                      textDecoration: 'none',
                      fontSize: '0.65rem',
                      lineHeight: 1.8,
                      whiteSpace: 'nowrap',
                      '&:hover': {
                        color: '#ffffff',
                        textDecoration: 'underline',
                      },
                    }}
                  >
                    {link.label}
                  </Link>
                ))}
              </Box>
            </Box>
          </Box>

          {/* Other categories */}
          {Object.entries(footerLinks).filter(([category]) => category !== 'Useful Links').map(([category, links]) => (
            <Box key={category}>
              <Typography
                sx={{
                  fontWeight: 600,
                  fontSize: '0.7rem',
                  mb: 0.75,
                  textTransform: 'uppercase',
                  letterSpacing: '0.05em',
                }}
              >
                {category}
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.25 }}>
                {links.map((link, index) => (
                  link.isText ? (
                    <Typography
                      key={link.label}
                      sx={{
                        color: '#ffffff',
                        fontSize: '0.65rem',
                        lineHeight: 1.3,
                        fontWeight: link.isTitle ? 600 : 400,
                        mb: (category === 'Contact Info' && index === 1) || (category === 'Working Hours' && index === 1) ? 0.5 : 0,
                        mt: link.isTitle && index > 0 ? 0.5 : 0,
                      }}
                    >
                      {link.label}
                    </Typography>
                  ) : (
                    <Link
                      key={link.label}
                      href={link.href}
                      sx={{
                        color: '#ffffff',
                        textDecoration: 'none',
                        fontSize: '0.65rem',
                        lineHeight: 1.3,
                        '&:hover': {
                          color: '#ffffff',
                          textDecoration: 'underline',
                        },
                      }}
                    >
                      {link.label}
                    </Link>
                  )
                ))}
              </Box>
            </Box>
          ))}

          {/* Social Media - Compact */}
          <Box>
            {(socialMedia?.facebook || socialMedia?.instagram || socialMedia?.tikTok || socialMedia?.x || socialMedia?.linkedIn) && (
              <>
                <Typography
                  sx={{
                    fontWeight: 600,
                    fontSize: '0.7rem',
                    mb: 0.75,
                    textTransform: 'uppercase',
                    letterSpacing: '0.05em',
                  }}
                >
                  Follow Us
                </Typography>
                <Box sx={{ display: 'flex', gap: 1.5, mb: 1 }}>
                  {socialMedia?.facebook && (
                    <IconButton href={socialMedia.facebook} target="_blank" rel="noreferrer" aria-label="Facebook" size="small" sx={{ color: '#ffffff', p: 0.25 }}>
                      <FacebookIcon fontSize="small" />
                    </IconButton>
                  )}
                  {socialMedia?.instagram && (
                    <IconButton href={socialMedia.instagram} target="_blank" rel="noreferrer" aria-label="Instagram" size="small" sx={{ color: '#ffffff', p: 0.25 }}>
                      <InstagramIcon fontSize="small" />
                    </IconButton>
                  )}
                  {socialMedia?.tikTok && (
                    <IconButton href={socialMedia.tikTok} target="_blank" rel="noreferrer" aria-label="TikTok" size="small" sx={{ color: '#ffffff', p: 0.25 }}>
                      <TikTokIcon fontSize="small" />
                    </IconButton>
                  )}
                  {socialMedia?.x && (
                    <IconButton href={socialMedia.x} target="_blank" rel="noreferrer" aria-label="X" size="small" sx={{ color: '#ffffff', p: 0.25 }}>
                      <XIcon fontSize="small" />
                    </IconButton>
                  )}
                  {socialMedia?.linkedIn && (
                    <IconButton href={socialMedia.linkedIn} target="_blank" rel="noreferrer" aria-label="LinkedIn" size="small" sx={{ color: '#ffffff', p: 0.25 }}>
                      <LinkedInIcon fontSize="small" />
                    </IconButton>
                  )}
                </Box>
              </>
            )}
          </Box>
        </Box>

        {/* Bottom Section - Three Columns */}
        <Box sx={{
          borderTop: '1px solid rgba(255,255,255,0.1)',
          pt: 2,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          flexWrap: 'wrap',
          gap: 1,
        }}>
          {/* Left - Copyright */}
          <Typography sx={{ fontSize: '0.65rem', color: '#ffffff' }}>
            © {new Date().getFullYear()} {businessName || 'Store'}. All Rights Reserved.
          </Typography>

          {/* Center - Payment Methods */}
          <Typography sx={{ fontSize: '0.65rem', color: '#ffffff' }}>
            Payment Methods: M-Pesa • Visa • Mastercard
          </Typography>

          {/* Right - Links */}
          <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
            <Link
              href="/terms"
              sx={{
                fontSize: '0.65rem',
                color: '#ffffff',
                textDecoration: 'none',
                '&:hover': { color: '#ffffff' },
              }}
            >
              Terms of Use
            </Link>
            <Typography sx={{ fontSize: '0.65rem', color: '#ffffff' }}>•</Typography>
            <Link
              href="/privacy"
              sx={{
                fontSize: '0.65rem',
                color: '#ffffff',
                textDecoration: 'none',
                '&:hover': { color: '#ffffff' },
              }}
            >
              Privacy Policy
            </Link>
          </Box>
        </Box>
      </Container>
    </Box>
  );
};

export default Footer;
